import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Minus, Plus, ShoppingCart, Heart } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import ProductCard, { BADGES } from "@/components/ProductCard";
import ProductImage from "@/components/ProductImage";
import { OctopusMascot } from "@/components/Mascot";
import { api, formatPrice, effectivePrice, waLink, imgUrl, colorHex } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

export default function ProductDetail() {
  const { id } = useParams();
  const { addToCart, content, categories, favorites, toggleFavorite } = useStore();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [color, setColor] = useState("");
  const [variation, setVariation] = useState("");

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setActiveImg(0);
    setQty(1);
    setColor("");
    setVariation("");
    api.get(`/products/${id}`)
      .then(({ data }) => {
        setProduct(data);
        if (data.category_id) {
          api.get("/products", { params: { category: data.category_id } })
            .then(({ data: rel }) => setRelated(rel.filter((r) => r.id !== data.id).slice(0, 4)))
            .catch(() => {});
        }
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="max-w-7xl mx-auto px-4 py-24 flex flex-col items-center text-center gap-4">
          <OctopusMascot size={110} />
          <p className="font-bold text-slate-800 text-xl">Produto não encontrado</p>
          <Link to="/produtos" className="bg-brand-gradient text-white font-bold px-6 py-3 rounded-full text-sm">Ver produtos</Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="max-w-7xl mx-auto px-4 py-16">
          <div className="grid lg:grid-cols-2 gap-10">
            <div className="aspect-square rounded-3xl bg-slate-100 animate-pulse" />
            <div className="space-y-4">
              <div className="h-8 w-2/3 bg-slate-100 rounded-xl animate-pulse" />
              <div className="h-5 w-1/3 bg-slate-100 rounded-xl animate-pulse" />
              <div className="h-24 w-full bg-slate-100 rounded-xl animate-pulse" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  const hasPromo = product.promo_price && product.promo_price > 0 && product.promo_price < product.price;
  const badge = BADGES[product.badge];
  const category = categories.find((c) => c.id === product.category_id);
  const isFav = favorites.includes(product.id);
  const images = product.images?.length ? product.images : [""];

  const buyWhatsApp = () => {
    const extras = [color && `Cor: ${color}`, variation && variation].filter(Boolean).join(" | ");
    const pricePart = effectivePrice(product) > 0 ? ` — ${formatPrice(effectivePrice(product) * qty)}` : "";
    const msg = `Olá, Art3D! Quero comprar:\n- ${qty}x ${product.name}${extras ? ` (${extras})` : ""}${pricePart}`;
    window.open(waLink(content, msg), "_blank");
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link to="/produtos" className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-500 hover:text-pink-600 transition-colors mb-6" data-testid="back-to-products">
          <ArrowLeft size={16} /> Voltar aos produtos
        </Link>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14">
          {/* Galeria */}
          <div data-testid="product-gallery">
            <div className="relative aspect-square rounded-3xl overflow-hidden shadow-card bg-slate-50">
              <ProductImage src={images[activeImg]} alt={product.name} />
              {badge && (
                <span className={`absolute top-4 left-4 text-xs font-bold px-3 py-1.5 rounded-full ${badge.cls}`}>{badge.label}</span>
              )}
              {!product.available && (
                <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center">
                  <span className="bg-slate-800 text-white text-sm font-bold px-4 py-2 rounded-full">Esgotado</span>
                </div>
              )}
            </div>
            {product.images?.length > 1 && (
              <div className="flex gap-3 mt-4">
                {product.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    data-testid={`gallery-thumb-${i}`}
                    className={`w-20 h-20 rounded-2xl overflow-hidden border-2 transition-colors ${activeImg === i ? "border-pink-400" : "border-transparent"}`}
                  >
                    <img src={imgUrl(img)} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Infos */}
          <div className="flex flex-col" data-testid="product-info">
            {category && (
              <Link to={`/produtos?category=${category.id}`} className="text-xs font-bold text-purple-600 bg-purple-50 self-start px-3 py-1.5 rounded-full hover:bg-purple-100 transition-colors" data-testid="product-category">
                {category.name}
              </Link>
            )}
            <div className="flex items-start justify-between gap-3 mt-3">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900" data-testid="product-name">{product.name}</h1>
              <button
                onClick={() => toggleFavorite(product.id)}
                aria-label="Favoritar"
                data-testid="detail-favorite-btn"
                className={`w-11 h-11 shrink-0 rounded-full flex items-center justify-center shadow-soft transition-all active:scale-90 ${isFav ? "bg-pink-500 text-white" : "bg-white text-slate-400 hover:text-pink-500"}`}
              >
                <Heart size={20} fill={isFav ? "currentColor" : "none"} />
              </button>
            </div>

            <div className="mt-4 flex items-baseline gap-3">
              {hasPromo && <span className="text-lg text-slate-400 line-through">{formatPrice(product.price)}</span>}
              <span className={`font-black ${hasPromo ? "text-pink-600" : "text-slate-900"} ${effectivePrice(product) > 0 ? "text-3xl" : "text-xl"}`} data-testid="product-price">
                {formatPrice(effectivePrice(product))}
              </span>
              {hasPromo && <span className="bg-brand-gradient text-white text-xs font-bold px-2.5 py-1 rounded-full">Promoção</span>}
            </div>

            {product.description && (
              <p className="mt-5 text-slate-600 leading-relaxed whitespace-pre-line" data-testid="product-description">{product.description}</p>
            )}

            {product.colors?.length > 0 && (
              <div className="mt-6">
                <p className="text-sm font-bold text-slate-700 mb-2.5">Cor {color && <span className="font-normal text-slate-400">— {color}</span>}</p>
                <div className="flex flex-wrap gap-2">
                  {product.colors.map((c) => (
                    <button
                      key={c}
                      onClick={() => setColor(c === color ? "" : c)}
                      data-testid={`color-option-${c}`}
                      className={`flex items-center gap-2 px-3.5 py-2 rounded-full border-2 text-sm font-semibold transition-all active:scale-95 ${
                        color === c ? "border-pink-400 bg-pink-50 text-pink-700" : "border-slate-200 text-slate-600 hover:border-pink-200"
                      }`}
                    >
                      <span className="w-4 h-4 rounded-full border border-slate-200" style={{ backgroundColor: colorHex(c) }} />
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {product.variations?.length > 0 && (
              <div className="mt-6">
                <p className="text-sm font-bold text-slate-700 mb-2.5">Opções</p>
                <div className="flex flex-wrap gap-2">
                  {product.variations.map((v) => (
                    <button
                      key={v}
                      onClick={() => setVariation(v === variation ? "" : v)}
                      data-testid={`variation-option-${v}`}
                      className={`px-4 py-2 rounded-full border-2 text-sm font-semibold transition-all active:scale-95 ${
                        variation === v ? "border-purple-400 bg-purple-50 text-purple-700" : "border-slate-200 text-slate-600 hover:border-purple-200"
                      }`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-7 flex items-center gap-4">
              <div className="flex items-center gap-3 bg-slate-100 rounded-full px-2 py-1.5">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Diminuir" data-testid="qty-dec" className="w-8 h-8 rounded-full bg-white shadow-soft flex items-center justify-center text-slate-600 active:scale-90 transition-transform">
                  <Minus size={15} />
                </button>
                <span className="font-bold w-6 text-center" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} aria-label="Aumentar" data-testid="qty-inc" className="w-8 h-8 rounded-full bg-white shadow-soft flex items-center justify-center text-slate-600 active:scale-90 transition-transform">
                  <Plus size={15} />
                </button>
              </div>
              <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${product.available ? "bg-green-50 text-green-600" : "bg-slate-100 text-slate-500"}`} data-testid="availability-badge">
                {product.available ? "● Disponível" : "● Esgotado"}
              </span>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                disabled={!product.available}
                onClick={() => addToCart(product, qty, color, variation)}
                data-testid="detail-add-cart-btn"
                className={`flex-1 font-bold py-4 rounded-full inline-flex items-center justify-center gap-2 transition-all duration-200 active:scale-95 ${
                  product.available ? "bg-brand-gradient text-white shadow-soft hover:opacity-90" : "bg-slate-100 text-slate-400 cursor-not-allowed"
                }`}
              >
                <ShoppingCart size={18} />
                Adicionar ao carrinho
              </button>
              <button
                onClick={buyWhatsApp}
                data-testid="detail-buy-whatsapp-btn"
                className="flex-1 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold py-4 rounded-full inline-flex items-center justify-center gap-2 transition-all duration-200 active:scale-95 shadow-soft"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm5.4 14.1c-.2.7-1.3 1.3-1.9 1.4-.5.1-1.1.1-1.8-.1-.4-.1-1-.3-1.7-.6-3-1.3-4.9-4.3-5.1-4.5-.1-.2-1.2-1.6-1.2-3s.7-2.1 1-2.4c.2-.3.5-.3.7-.3h.5c.2 0 .4 0 .6.4l.9 2.1c.1.2.1.4 0 .6l-.4.6c-.1.2-.3.4-.1.7.1.3.7 1.1 1.5 1.8 1 .9 1.9 1.2 2.1 1.3.3.1.5.1.6-.1l.8-1c.2-.2.4-.2.6-.1l2 1c.3.1.5.2.5.4.1.1.1.6-.1 1.2Z"/></svg>
                Comprar pelo WhatsApp
              </button>
            </div>
          </div>
        </div>

        {/* Relacionados */}
        {related.length > 0 && (
          <section className="mt-20" data-testid="related-products">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">Você também pode gostar 💗</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {related.map((p) => (
                <ProductCard key={p.id} product={p} onAdd={(prod) => addToCart(prod, 1)} />
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
      <CartDrawer />
      <WhatsAppFloat />
    </div>
  );
}
